import { useState, useEffect } from "react";
import { ReactMultiEmail } from "react-multi-email";
import { Fragment } from "react";
import { FormGroup, Input, Label, Modal } from "reactstrap";
import { toast } from "react-toastify";
import { sendReportingMail } from "../services/users.js";
import { formattedDate } from "./formatted/FormattedDateAndTime.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

const PdfModal = ({
  data,
  setOpenModalExport,
  openModalExport,
  setLoadScreen,
  setPdfFile,
  pdfFile,
  characterization,
  pdfDateTime,
  initialAction = "export",
}) => {
  const { t, i18n } = useTranslation();
  const [action, setAction] = useState(initialAction);
  const [userToSendMail, setUserToSendMail] = useState([]);

  // Update action when initialAction changes
  useEffect(() => {
    setAction(initialAction);
  }, [initialAction]);
  const [subject, setSubject] = useState(() => {
    if (characterization) {
      return `${t("Characterization Report of container")} ${data.wasteName} ${data?.positioning || ""}`;
    }
    return `${t("Compliance Report of container")} ${data.wasteName} ${data?.positioning || ""}`;
  });

  const [message, setMessage] = useState(() => {
    if (characterization) {
      return `${t("Hello")},\n\n${t("Please find attached your characterization report for container")} ${data.wasteName} (${formattedDate(characterization ? pdfDateTime : data._time, i18n.language, true)}).`;
    }
    return `${t("Hello")},\n\n${t("Please find attached your compliance report for container")} ${data.wasteName} (${formattedDate(data._time, i18n.language, true)}).`;
  });

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const toggle = () => {
    setAction(initialAction);
    setUserToSendMail([]);
    setOpenModalExport(!openModalExport);
    if (characterization) {
      setSubject(
        `${t("Characterization Report of container")} ${data.wasteName} ${data?.positioning || ""}`,
      );
      setMessage(
        `${t("Hello")},\n\n${t("Please find attached your characterization report for container")} ${data.wasteName} (${formattedDate(pdfDateTime, i18n.language, true)}).`,
      );
    } else {
      setSubject(
        `${t("Compliance Report of container")} ${data.wasteName} ${data?.positioning || ""}`,
      );
      setMessage(
        `${t("Hello")},\n\n${t("Please find attached your compliance report for container")} ${data.wasteName} (${formattedDate(data._time, i18n.language, true)}).`,
      );
    }
  };

  const sendMail = async () => {
    setLoadScreen(true);
    try {
      const attachments = await Promise.all(
        pdfFile.map(async (file) => ({
          filename: file.filename,
          mimetype: "application/pdf",
          content: await blobToBase64(file.pdfBlob),
        })),
      );

      const payload = {
        usersToSendMail: userToSendMail,
        subject,
        message,
        attachments,
      };

      await sendReportingMail(payload);

      toast.success(t("Email sent successfully"), {
        className: "custom-toast",
      });
    } catch (err) {
      console.error(`${t("Error sending PDF")} :`, err);
      toast.error(t("Error sending the email"));
    }
    toggle();
    setPdfFile([]);
    setLoadScreen(false);
  };

  const handleRemovePdf = (indexToRemove) => {
    setPdfFile((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const downloadPdf = async (filesArray) => {
    setLoadScreen(true);

    filesArray.forEach((file) => {
      const blob =
        file.pdfBlob instanceof Blob
          ? file.pdfBlob
          : new Blob([file.pdfBlob], { type: "application/pdf" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    toggle();
    setLoadScreen(false);
    setPdfFile([]);
    toast.success(t("Your file has been successfully downloaded"), {
      className: "custom-toast",
    });
  };

  return (
    <Modal
      isOpen={openModalExport}
      toggle={() => toggle()}
      contentClassName="rounded-lg"
      className="w-2/6"
    >
      <section className="flex justify-between py-2 px-2 border-b-1">
        <h5>{`${t("Export")} / ${t("Share")}`}</h5>
        <FontAwesomeIcon
          icon="times"
          className="close-modal"
          onClick={() => toggle()}
        />
      </section>
      <section className="bg-white rounded-md p-3 w-auto">
        <FormGroup>
          <Input
            id="action"
            name="action"
            type="select"
            onChange={(event) => {
              setAction(event.target.value);
              setUserToSendMail([]);
            }}
            value={action}
          >
            <option value="export">{`${t("Export")} PDF (.pdf)`}</option>
            <option value="share">{t("Share by email")}</option>
          </Input>
        </FormGroup>
        {action === "share" && (
          <Fragment>
            <FormGroup>
              <Label for="user">{t("Recipient")}</Label>
              <ReactMultiEmail
                placeholder={t("Recipient")}
                emails={userToSendMail}
                onChange={(emails) => setUserToSendMail(emails)}
                autoFocus={true}
                getLabel={(email, index, removeEmail) => {
                  return (
                    <div data-tag key={index}>
                      <div data-tag-item>{email}</div>
                      <span data-tag-handle onClick={() => removeEmail(index)}>
                        ×
                      </span>
                    </div>
                  );
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label for="subject">{t("Subject")}</Label>
              <Input
                className="text-sm"
                id="subject"
                name="subject"
                placeholder={t("Subject")}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="message">{t("Message")}</Label>
              <Input
                className="text-sm h-24"
                id="message"
                name="message"
                placeholder={t("Message")}
                type="textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </FormGroup>
          </Fragment>
        )}

        {pdfFile.length > 0 && (
          <FormGroup>
            <Label htmlFor="file">{t("File")}</Label>
            {pdfFile.map((pdf, index) => (
              <div className="flex justify-between py-1 px-2 items-center border-t-1 border-gray-200">
                <div className="flex flex-row gap-2 items-center">
                  <i className="fas fa-file-pdf text-red-500"></i>
                  <a
                    href={URL.createObjectURL(pdf.pdfBlob)}
                    download={pdf.filename}
                    className="text-blue-600 hover:underline"
                  >
                    {pdf.filename}
                  </a>
                </div>
                <i
                  className="fas fa-times text-gray-500 hover:text-red cursor-pointer"
                  onClick={() => handleRemovePdf(index)}
                ></i>
              </div>
            ))}
          </FormGroup>
        )}
      </section>

      <div className="w-full flex justify-end gap-2 pr-3 py-2 border-t-1">
        {action === "export" ? (
          <>
            <button
              type="button"
              className={`p-2 text-sm font-medium text-white rounded-sm ${
                pdfFile.length > 0 ? "bg-[#00A7A6]" : "bg-[#DCDCDC]"
              }`}
              onClick={() => downloadPdf(pdfFile)}
            >
              {t("Export")}
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`p-2 text-sm font-medium text-white rounded-sm ${
              userToSendMail.length > 0 ? "bg-[#00A7A6]" : "bg-[#DCDCDC]"
            }`}
            onClick={sendMail}
          >
            {t("Share")}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default PdfModal;
